// Shared vocabulary for the Habit Stats completion-trend chart.
//
// These string values ARE the backend wire contract: `RangeOption` values map
// 1:1 to the `range` query param (Lifetime -> "all"), and `HealthLens` values
// map 1:1 to the `algorithm` query param of GET /api/habits/{id}/stats/health.
// Lifting them here avoids an import cycle between HabitStats.tsx and the
// preferences hook.

export type RangeOption =
  | '7d'
  | '30d'
  | '90d'
  | '180d'
  | '365d'
  | '3y'
  | '5y'
  | '10y'
  | 'all' // "Lifetime"
  | 'custom';

export type GranularityOption = 'day' | 'week' | 'month' | 'period';

export type HealthLens =
  | 'completion_rate_by_period' // legacy completion rate
  | 'closed_period_adherence' // default
  | 'rolling_adherence'
  | 'momentum'
  | 'trend_direction'
  | 'target_fulfillment'
  | 'streak_gap_health'
  | 'volatility_stability'
  | 'lifetime_fulfillment';

export const RANGE_ORDER: RangeOption[] = [
  '7d',
  '30d',
  '90d',
  '180d',
  '365d',
  '3y',
  '5y',
  '10y',
  'all',
  'custom',
];

export const RANGE_LABELS: Record<RangeOption, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '180d': '180 days',
  '365d': '365 days',
  '3y': '3 years',
  '5y': '5 years',
  '10y': '10 years',
  all: 'Lifetime',
  custom: 'Custom…',
};

// Day-count spans for the fixed ranges (mirrors the backend day-count aliases).
export const RANGE_SPAN_DAYS: Record<
  Exclude<RangeOption, 'all' | 'custom'>,
  number
> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '180d': 180,
  '365d': 365,
  '3y': 1095,
  '5y': 1825,
  '10y': 3650,
};

export const DEFAULT_HEALTH_LENS: HealthLens = 'closed_period_adherence';

export interface HealthLensSummary {
  score: number;
  eligible_periods: number;
  completed_periods: number;
  sample_size: number;
  min_sample_size: number;
  low_confidence: boolean;
  window?: number | null;
  span?: number | null;
  slope?: number | null;
  intercept?: number | null;
  r_squared?: number | null;
  direction?: string | null;
  volatility?: number | null;
  stability?: number | null;
  current_streak?: number | null;
  longest_streak?: number | null;
  current_gap?: number | null;
  avg_gap?: number | null;
  recovery_rate?: number | null;
  measurable?: boolean | null;
  fulfilled_value?: number | null;
  target_value_total?: number | null;
}

export interface LensConfig {
  label: string;
  description: string;
  // Extra one-liner shown beside the headline score for lenses that carry
  // summary metrics that aren't themselves the 0-100 score.
  summaryNote?: (summary: HealthLensSummary) => string | null;
}

const pct = (v: number | null | undefined): string =>
  v == null ? '—' : `${Math.round(v)}%`;

export const LENS_ORDER: HealthLens[] = [
  'completion_rate_by_period',
  'closed_period_adherence',
  'rolling_adherence',
  'momentum',
  'trend_direction',
  'target_fulfillment',
  'streak_gap_health',
  'volatility_stability',
  'lifetime_fulfillment',
];

export const LENS_CONFIG: Record<HealthLens, LensConfig> = {
  completion_rate_by_period: {
    label: 'Completion Rate by Period',
    description:
      'Share of each period’s target you completed — the classic completion-rate chart.',
  },
  closed_period_adherence: {
    label: 'Closed-Period Adherence',
    description:
      'Partial-credit adherence across fully-elapsed recurrence periods (excludes the current in-progress period).',
  },
  rolling_adherence: {
    label: 'Rolling Adherence',
    description: 'Adherence smoothed over a trailing window of periods.',
    summaryNote: (s) => (s.window ? `${s.window}-period rolling average` : null),
  },
  momentum: {
    label: 'Momentum',
    description:
      'Exponentially-weighted recent adherence — reacts quickly to your latest periods.',
    summaryNote: (s) => (s.span ? `EMA span ${s.span}` : null),
  },
  trend_direction: {
    label: 'Trend Direction',
    description:
      'Fitted trend line of adherence over time; the slope shows whether the habit is improving.',
    summaryNote: (s) => {
      if (s.slope == null || !s.direction) return null;
      const sign = s.slope > 0 ? '+' : '';
      return `${s.direction} · ${sign}${s.slope.toFixed(2)} pts/period`;
    },
  },
  target_fulfillment: {
    label: 'Target Fulfillment',
    description:
      'How much of your total target you fulfilled (value-based for measurable habits, otherwise counts).',
    summaryNote: (s) => (s.measurable ? 'Value-based (measurable)' : null),
  },
  streak_gap_health: {
    label: 'Streak & Gap Health',
    description:
      'Composite of current streak, adherence, gap recovery, and how recently you lapsed.',
    summaryNote: (s) =>
      s.current_streak != null
        ? `Streak ${s.current_streak} · Longest ${s.longest_streak} · Recovery ${pct(
            (s.recovery_rate ?? 0) * 100,
          )}`
        : null,
  },
  volatility_stability: {
    label: 'Volatility / Stability',
    description:
      'How steady your adherence is. Higher score = more stable (lower volatility).',
    summaryNote: (s) =>
      s.volatility != null ? `Volatility ${s.volatility.toFixed(1)}` : null,
  },
  lifetime_fulfillment: {
    label: 'Lifetime Fulfillment',
    description:
      'Target fulfillment across the habit’s entire history (always the lifetime window).',
    summaryNote: (s) => (s.measurable ? 'Value-based (measurable)' : null),
  },
};
